import { ref, reactive } from 'vue';

export function useManualCardWizard(emit) {
    const manualStep = ref(1);
    const errors = ref([]);
    const fieldErrors = reactive({});
    const addCardBtnAction = ref(false);

    const manualCard = reactive({
        chosenCard: null,
        last_four: '',
        dateOpened: '',
        accountType: '',
        bonusAmount: '',
        minSpendDate: '',
        bonusReceived: '',
        dateReceived: '',
        cardStatus: 'open',
        dateClosed: '',
        creditLimit: 0,
        annualFee: '',
        dateBonusEarned: '',
        currentRewardsBalance: '',
        institution_code: '',
        card_not_found_name: '',
        card_not_found_bank: '',
        card_not_found: false,
        opened_long_ago: false,
        closed_long_ago: false,
        business_id: null,
        user_managed_annual_fee: false,
    });

    const manualChosenCard = ref(null);

    const stepsPercent = {
        1: 25,
        2: 35,
        3: 45,
        4: 55,
        5: 65,
        6: 75,
        7: 85,
        8: 100,
    };

    function clearFieldError(field) {
        if (fieldErrors[field]) {
            delete fieldErrors[field];
        }
    }

    function clearAllFieldErrors() {
        Object.keys(fieldErrors).forEach((key) => delete fieldErrors[key]);
    }

    function validateStep3() {
        clearAllFieldErrors();

        // Validate Date opened (required only if card status is open, unless checkbox is marked)
        if (
            manualCard.cardStatus === 'open' &&
            manualCard.dateOpened === '' &&
            !manualCard.opened_long_ago
        ) {
            fieldErrors.date_opened = 'Select date opened';
        }

        // Validate Date closed (required if card status is closed)
        if (
            manualCard.cardStatus === 'closed' &&
            manualCard.dateClosed === ''
        ) {
            fieldErrors.date_closed = 'Select date closed';
        }

        // Validate Account type (always required)
        if (manualCard.accountType === '') {
            fieldErrors.account_type = 'Select account type';
        }

        return Object.keys(fieldErrors).length === 0;
    }

    function gotoManualStep(step) {
        errors.value = [];
        clearAllFieldErrors();

        if (manualStep.value === 1 && step === 2) {
            // Validate card selection
            if (!manualCard.chosenCard && !manualCard.card_not_found) {
                fieldErrors.card_selection = 'Select card to continue';
            }

            // Validate missing card name if "card not found" is selected
            if (
                manualCard.card_not_found &&
                manualCard.card_not_found_name === ''
            ) {
                errors.value.push({
                    message: 'Please enter the card suggestion.',
                });
            }

            // Prevent advancing if there are field errors
            if (Object.keys(fieldErrors).length > 0) {
                return;
            }

            // Skip step 2 (date opened) and go directly to step 3
            if (errors.value.length === 0) {
                step = 3;
            }
        }

        if (manualStep.value === 2 && step === 3) {
            if (
                manualCard.dateOpened === '' &&
                !manualCard.opened_long_ago
            ) {
                fieldErrors.date_opened = 'Select date opened';
            }

            if (Object.keys(fieldErrors).length > 0) {
                return;
            }
        }

        // Skip step 2 when going back from step 3 to step 2
        if (manualStep.value === 3 && step === 2) {
            step = 1;
        }

        if (manualStep.value === 3 && step === 4) {
            if (!validateStep3()) {
                return;
            }

            // If authorized user, go to step 4
            if (manualCard.accountType === 'authorized_user') {
                emit('setProgress', 100);
                manualStep.value = 4;
                return;
            }
        }

        if (errors.value.length === 0) {
            emit('setProgress', stepsPercent[step]);
            manualStep.value = step;
        }
    }

    function cardSelected(card) {
        manualChosenCard.value = card;
        manualCard.institution_code = card.issuing_bank?.institution_code ?? '';
        manualCard.chosenCard = card.id;
        manualCard.annualFee = card.annual_fee;
        // Clear validation error when card is selected
        clearFieldError('card_selection');
    }

    function clearSelectedCard() {
        manualChosenCard.value = null;
        manualCard.chosenCard = null;
        manualCard.institution_code = '';
    }

    function resetManualCard() {
        manualChosenCard.value = null;
        Object.assign(manualCard, {
            chosenCard: null,
            last_four: '',
            dateOpened: '',
            accountType: '',
            bonusAmount: '',
            minSpendDate: '',
            bonusReceived: '',
            dateReceived: '',
            cardStatus: 'open',
            dateClosed: '',
            creditLimit: 0,
            annualFee: '',
            dateBonusEarned: '',
            currentRewardsBalance: '',
            institution_code: '',
            card_not_found: false,
            card_not_found_name: '',
            card_not_found_bank: '',
            opened_long_ago: false,
            closed_long_ago: false,
            business_id: null,
            user_managed_annual_fee: false,
        });
    }

    return {
        manualStep,
        errors,
        fieldErrors,
        addCardBtnAction,
        manualCard,
        manualChosenCard,
        gotoManualStep,
        validateStep3,
        clearFieldError,
        cardSelected,
        clearSelectedCard,
        resetManualCard,
    };
}
